import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Pagination,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  Collapse,
  Avatar as MuiAvatar
} from '@mui/material';
import {
  Add as AddIcon,
  CreditCard,
  Lock,
  Security,
  CheckCircle,
  AccountBalance,
  AccountCircle,
  Person,
  Send,
  ArrowBack,
  Notifications as NotificationsIcon,
  QrCodeScanner as QrCodeScannerIcon,
  CameraAlt as CameraAltIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import TransactionDetailPage from './TransactionDetail/TransactionDetailPage';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import {
  fetchCards,
  clearCardAddFlow,
  clearError,
  setDefaultCard,
  deleteCard,
  initializeCardLink,
  verifyCardLink,
} from '../store/slices/cardsSlice';
import {
  fetchBankAccounts,
  addBankAccount,
  resolveBankAccount,
  deleteBankAccount,
  setDefaultBankAccount,
} from '../store/slices/bankAccountsSlice';
import { paymentsAPI, usersAPI, transferPinAPI, bankAccountsAPI, newIdempotencyKey } from '../services/api';
import { updateUserField } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { fetchTransactions } from '../store/slices/transactionsSlice';
import {
  fetchKYCStatus,
  fetchKYCDocuments,
  uploadKYCDocument,
  deleteKYCDocument,
} from '../store/slices/kycSlice';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  setCurrentPage,
} from '../store/slices/notificationsSlice';

const EmailVerificationPage: React.FC = () => {
  const [status, setStatus] = useState<'pending' | 'verifying' | 'success' | 'error'>('pending');
  const [errorMsg, setErrorMsg] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  useEffect(() => {
    if (token) {
      setStatus('verifying');
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        setStatus('error');
        setErrorMsg('API URL not configured');
        return;
      }
      fetch(`${apiUrl}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setStatus('success');
          } else {
            setStatus('error');
            setErrorMsg(data.error?.message || 'Verification failed. The link may have expired.');
          }
        })
        .catch(() => {
          setStatus('error');
          setErrorMsg('Network error. Please try again.');
        });
    }
  }, [token]);

  const handleResend = async () => {
    if (!resendEmail) return;
    setResendStatus('sending');
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        setResendStatus('error');
        return;
      }
      const res = await fetch(`${apiUrl}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendEmail }),
      });
      const data = await res.json();
      setResendStatus(data.success ? 'sent' : 'error');
    } catch {
      setResendStatus('error');
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #09486D 0%, #11C1EC 100%)',
      p: 3
    }}>
      <Paper sx={{
        p: 5,
        textAlign: 'center',
        borderRadius: 4,
        maxWidth: 480,
        width: '100%',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
      }}>
        {status === 'verifying' && (
          <>
            <CircularProgress size={56} sx={{ color: '#11C1EC', mb: 3 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#202647', mb: 2 }}>
              Verifying your email...
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <Box sx={{
              width: 72, height: 72, borderRadius: '50%',
              background: 'linear-gradient(135deg, #27ae60, #2ecc71)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3
            }}>
              <Typography sx={{ fontSize: 36, color: 'white' }}>&#10003;</Typography>
            </Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#202647', mb: 2 }}>
              Email Verified!
            </Typography>
            <Typography variant="body1" sx={{ color: '#7F839C', mb: 3, lineHeight: 1.6 }}>
              Your email has been verified successfully. You can now sign in to your MalPay account.
            </Typography>
            <Button
              variant="contained"
              href="/login"
              sx={{
                background: 'linear-gradient(135deg, #11C1EC, #09486D)',
                color: 'white', borderRadius: 3, px: 5, py: 1.5, textTransform: 'none', fontWeight: 600,
                '&:hover': { background: 'linear-gradient(135deg, #0eadd4, #073d5c)' },
              }}
            >
              Sign In
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Box sx={{
              width: 72, height: 72, borderRadius: '50%',
              background: 'linear-gradient(135deg, #e74c3c, #c0392b)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3
            }}>
              <Typography sx={{ fontSize: 36, color: 'white' }}>&#10007;</Typography>
            </Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#202647', mb: 2 }}>
              Verification Failed
            </Typography>
            <Typography variant="body1" sx={{ color: '#7F839C', mb: 3, lineHeight: 1.6 }}>
              {errorMsg}
            </Typography>
            <TextField
              fullWidth size="small" placeholder="Enter your email to resend"
              value={resendEmail} onChange={(e) => setResendEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained" fullWidth onClick={handleResend}
              disabled={resendStatus === 'sending' || !resendEmail}
              sx={{
                background: 'linear-gradient(135deg, #11C1EC, #09486D)',
                color: 'white', borderRadius: 3, py: 1.2, textTransform: 'none', fontWeight: 600, mb: 2,
                '&:hover': { background: 'linear-gradient(135deg, #0eadd4, #073d5c)' },
              }}
            >
              {resendStatus === 'sending' ? 'Sending...' : resendStatus === 'sent' ? 'Email Sent!' : 'Resend Verification Email'}
            </Button>
            <Button variant="text" href="/login" sx={{ color: '#11C1EC', textTransform: 'none' }}>
              Back to Sign In
            </Button>
          </>
        )}

        {status === 'pending' && (
          <>
            <Box sx={{
              width: 72, height: 72, borderRadius: '50%',
              background: 'linear-gradient(135deg, #11C1EC, #09486D)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3
            }}>
              <Typography sx={{ fontSize: 36, color: 'white' }}>&#9993;</Typography>
            </Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#202647', mb: 2 }}>
              Check Your Email
            </Typography>
            <Typography variant="body1" sx={{ color: '#7F839C', mb: 3, lineHeight: 1.6 }}>
              We've sent a verification link to your email address. Please check your inbox and click the link to activate your account.
            </Typography>
            <Typography variant="body2" sx={{ color: '#A0A3B5', mb: 3 }}>
              Didn't receive the email? Check your spam folder or enter your email below to resend.
            </Typography>
            <TextField
              fullWidth size="small" placeholder="Enter your email to resend"
              value={resendEmail} onChange={(e) => setResendEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            {resendStatus === 'sent' && (
              <Alert severity="success" sx={{ mb: 2 }}>Verification email sent! Check your inbox.</Alert>
            )}
            {resendStatus === 'error' && (
              <Alert severity="error" sx={{ mb: 2 }}>Failed to send. Please try again.</Alert>
            )}
            <Button
              variant="contained" fullWidth onClick={handleResend}
              disabled={resendStatus === 'sending' || !resendEmail}
              sx={{
                background: 'linear-gradient(135deg, #11C1EC, #09486D)',
                color: 'white', borderRadius: 3, py: 1.2, textTransform: 'none', fontWeight: 600, mb: 2,
                '&:hover': { background: 'linear-gradient(135deg, #0eadd4, #073d5c)' },
              }}
            >
              {resendStatus === 'sending' ? 'Sending...' : 'Resend Verification Email'}
            </Button>
            <Button variant="text" href="/login" sx={{ color: '#11C1EC', textTransform: 'none' }}>
              Back to Sign In
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

const ProfileCompletionPage: React.FC = () => {
  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' , borderRadius: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#202647', mb: 3, fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>
          Complete Your Profile
        </Typography>
        <Typography variant="body1">
          Please complete your profile by adding a bank account and at least one card.
        </Typography>
      </Paper>
    </Box>
  );
};

const CardsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { cards, error } = useSelector((state: RootState) => state.cards);

  // Card linking happens on Paystack's hosted page (/cards/add). MalPay never
  // receives the card number or CVV.
  const handleAddCard = () => {
    navigate('/cards/add');
  };

  useEffect(() => {
    dispatch(clearError());
    dispatch(fetchCards());
  }, [dispatch]);

  const handleSetDefaultCard = async (cardId: string) => {
    await dispatch(setDefaultCard(cardId));
  };

  const handleDeleteCard = async (cardId: string) => {
    if (window.confirm('Are you sure you want to remove this card?')) {
      await dispatch(deleteCard(cardId));
    }
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Mobile App Bar */}
      <Box sx={{
        bgcolor: 'white',
        px: 2,
        py: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: 1
      }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#202647' }}>
          Your Cards
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCard}
          sx={{ borderRadius: 3 }}
        >
          Add Card
        </Button>
      </Box>

      <Box sx={{ p: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {cards.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>
              No cards added yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add your first card to start making payments
            </Typography>
            <Button variant="contained" onClick={handleAddCard} sx={{ borderRadius: 3 }}>
              Add Your First Card
            </Button>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {cards.map((card) => (
              <Grid size={12} key={card.id}>
                <Card
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 3,
                    background: card.cardType === 'visa'
                      ? 'linear-gradient(135deg, #09486D 0%, #0a5d8a 50%, #11C1EC 100%)'
                      : card.cardType === 'mastercard'
                      ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
                      : 'linear-gradient(135deg, #1b1b2f 0%, #162447 50%, #1f4068 100%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    maxWidth: { xs: '100%', sm: 480 },
                    aspectRatio: '1.586/1',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-30%',
                      right: '-20%',
                      width: '60%',
                      height: '80%',
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <CardBrandLogo brand={card.cardType === 'visa' ? 'VISA' : card.cardType === 'mastercard' ? 'Mastercard' : card.cardType === 'amex' ? 'AMEX' : card.cardType.toUpperCase()} size={50} />
                    {card.isDefault && (
                      <Chip
                        label="Default"
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          fontWeight: 600,
                          backdropFilter: 'blur(4px)',
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily: '"Courier New", monospace', fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.6rem' }, letterSpacing: '0.15em', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                      {card.cardNumberMasked}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.1em', display: 'block', mb: 0.25 }}>
                        Card Holder
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                        {card.cardholderName}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.1em', display: 'block', mb: 0.25 }}>
                        Expires
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                        {card.expiryMonth.toString().padStart(2, '0')}/{card.expiryYear}
                      </Typography>
                    </Box>
                    <CardBrandLogo brand={card.cardType === 'visa' ? 'VISA' : card.cardType === 'mastercard' ? 'Mastercard' : card.cardType === 'amex' ? 'AMEX' : card.cardType.toUpperCase()} size={40} />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 0.5 }}>
                    {!card.isDefault && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSetDefaultCard(card.id)}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.15)',
                          color: 'white',
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          backdropFilter: 'blur(4px)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        Set Default
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleDeleteCard(card.id)}
                      sx={{
                        bgcolor: 'rgba(239,68,68,0.3)',
                        color: '#fca5a5',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        backdropFilter: 'blur(4px)',
                        '&:hover': { bgcolor: 'rgba(239,68,68,0.5)' },
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

      </Box>
    </Box>
  );
};

// Card brand logo SVG components
const CardBrandLogo: React.FC<{ brand: string; size?: number }> = ({ brand, size = 40 }) => {
  if (brand === 'VISA') {
    return (
      <Box sx={{ width: size, height: size * 0.6, bgcolor: 'white', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.5 }}>
        <Typography sx={{ fontSize: size * 0.35, fontWeight: 900, fontStyle: 'italic', color: '#1A1F71', letterSpacing: -1 }}>VISA</Typography>
      </Box>
    );
  }
  if (brand === 'Mastercard') {
    return (
      <Box sx={{ width: size, height: size * 0.6, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <Box sx={{ width: size * 0.4, height: size * 0.4, borderRadius: '50%', bgcolor: '#EB001B', position: 'absolute', left: size * 0.05 }} />
        <Box sx={{ width: size * 0.4, height: size * 0.4, borderRadius: '50%', bgcolor: '#F79E1B', position: 'absolute', right: size * 0.05, opacity: 0.85 }} />
      </Box>
    );
  }
  if (brand === 'AMEX') {
    return (
      <Box sx={{ width: size, height: size * 0.6, bgcolor: '#006FCF', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: size * 0.22, fontWeight: 800, color: 'white', letterSpacing: 0.5 }}>AMEX</Typography>
      </Box>
    );
  }
  if (brand === 'Verve') {
    return (
      <Box sx={{ width: size, height: size * 0.6, bgcolor: '#00425F', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: size * 0.25, fontWeight: 700, color: '#E31837' }}>Verve</Typography>
      </Box>
    );
  }
  if (brand === 'Discover') {
    return (
      <Box sx={{ width: size, height: size * 0.6, bgcolor: '#fff', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: size * 0.2, fontWeight: 700, color: '#FF6000' }}>DISCOVER</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ width: size, height: size * 0.6, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CreditCard sx={{ fontSize: size * 0.4, color: 'white' }} />
    </Box>
  );
};

// Add Bank Account Dialog Component
interface AddBankDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (bankData: any) => void;
  isSubmitting: boolean;
}

const AddBankDialog: React.FC<AddBankDialogProps> = ({ open, onClose, onSubmit, isSubmitting }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [formData, setFormData] = useState({
    accountName: '',
    accountNumber: '',
    bankName: '',
    bankCode: ''
  });
  const [bankOptions, setBankOptions] = useState<Array<{ name: string; code: string }>>([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [resolved, setResolved] = useState(false);

  // Fetch banks from Paystack API when dialog opens
  useEffect(() => {
    if (open && bankOptions.length === 0) {
      const fetchBanks = async () => {
        setLoadingBanks(true);
        try {
          const response = await bankAccountsAPI.getBanks();
          const banks = response.data?.data || response.data || [];
          if (Array.isArray(banks) && banks.length > 0) {
            setBankOptions(banks.map((bank: any) => ({ name: bank.name, code: bank.code })).sort((a: any, b: any) => a.name.localeCompare(b.name)));
          }
        } catch (err) {
          console.error('Failed to fetch banks:', err);
        } finally {
          setLoadingBanks(false);
        }
      };
      fetchBanks();
    }
  }, [open]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value
    });

    if (validationErrors[field]) {
      setValidationErrors({
        ...validationErrors,
        [field]: ''
      });
    }

    // Auto-trigger resolve when account number reaches 10 digits and bank is selected
    if (field === 'accountNumber' && value.length === 10 && formData.bankCode) {
      handleResolveAccount(value, formData.bankCode);
    }
  };

  const handleResolveAccount = async (accountNum?: string, bankCode?: string) => {
    const accNum = accountNum || formData.accountNumber;
    const code = bankCode || formData.bankCode;

    if (!accNum || accNum.length < 10) {
      setValidationErrors({ accountNumber: 'Account number must be 10 digits' });
      return;
    }

    if (!code) {
      setValidationErrors({ bankCode: 'Please select a bank first' });
      return;
    }

    setResolving(true);
    try {
      const response: any = await dispatch(resolveBankAccount({
        accountNumber: accNum,
        bankCode: code,
      })).unwrap();

      if (response?.banks) {
        setBankOptions(response.banks.map((bank: any) => ({ name: bank.name, code: bank.code })));
      }

      if (response?.data) {
        setFormData(prev => ({
          accountNumber: response.data.accountNumber || prev.accountNumber,
          accountName: response.data.accountName || prev.accountName,
          bankName: response.data.bankName || prev.bankName,
          bankCode: response.data.bankCode || prev.bankCode,
        }));
        setResolved(true);
      }
    } catch (err: any) {
      setValidationErrors({
        accountNumber: err.response?.data?.error?.message || 'Failed to resolve account'
      });
    } finally {
      setResolving(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.accountName.trim()) {
      errors.accountName = 'Account name is required';
    }

    if (!formData.accountNumber.trim()) {
      errors.accountNumber = 'Account number is required';
    } else if (formData.accountNumber.length < 10) {
      errors.accountNumber = 'Account number must be 10 digits';
    }

    if (!formData.bankName) {
      errors.bankName = 'Bank selection is required';
    }

    if (!formData.bankCode) {
      errors.bankCode = 'Bank code is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const bankData = {
      accountName: formData.accountName,
      accountNumber: formData.accountNumber,
      bankName: formData.bankName,
      bankCode: formData.bankCode,
      isPrimary: false,
      isActive: true
    };

    onSubmit(bankData);

    // Reset form
    setFormData({
      accountName: '',
      accountNumber: '',
      bankName: '',
      bankCode: ''
    });
    setValidationErrors({});
    setResolved(false);
  };

  const nigerianBanks = bankOptions;

  const isFormValid = formData.accountName.trim().length > 0 &&
    formData.accountNumber.length === 10 &&
    formData.bankName &&
    formData.bankCode;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#202647', display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalance />
        Add Bank Account
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControl fullWidth error={!!validationErrors.bankName}>
                <InputLabel>{loadingBanks ? 'Loading banks...' : 'Bank'}</InputLabel>
                <Select
                  value={formData.bankName}
                  disabled={loadingBanks}
                  onChange={(e) => {
                    const selectedBank = nigerianBanks.find(bank => bank.name === e.target.value);
                    setFormData({
                      ...formData,
                      bankName: e.target.value,
                      bankCode: selectedBank?.code || ''
                    });
                    setValidationErrors({
                      ...validationErrors,
                      bankName: '',
                      bankCode: ''
                    });
                  }}
                  label={loadingBanks ? 'Loading banks...' : 'Bank'}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  {loadingBanks ? (
                    <MenuItem disabled>
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                      Loading banks from Paystack...
                    </MenuItem>
                  ) : nigerianBanks.length === 0 ? (
                    <MenuItem disabled>No banks available</MenuItem>
                  ) : (
                    nigerianBanks.map((bank) => (
                      <MenuItem key={bank.code} value={bank.name}>
                        {bank.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Account Number"
                value={formData.accountNumber}
                onChange={handleInputChange('accountNumber')}
                placeholder="1234567890"
                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                error={!!validationErrors.accountNumber}
                helperText={validationErrors.accountNumber || 'Enter 10-digit account number'}
              />
            </Grid>
            <Grid size={12}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleResolveAccount()}
                disabled={resolving || !formData.accountNumber || !formData.bankCode || formData.accountNumber.length < 10}
              >
                {resolving ? (
                  <>
                    <CircularProgress size={18} sx={{ mr: 1 }} />
                    Resolving...
                  </>
                ) : resolved ? (
                  <>
                    <CheckCircle sx={{ mr: 1, color: '#00C48C' }} />
                    Account Resolved
                  </>
                ) : (
                  'Resolve Account'
                )}
              </Button>
            </Grid>
            <Collapse in={resolved} sx={{ width: '100%' }}>
              <Grid size={12}>
                <Card sx={{
                  p: 2,
                  background: 'rgba(0, 196, 140, 0.05)',
                  border: '1px solid #00C48C'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircle sx={{ color: '#00C48C', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#00C48C' }}>
                      Account Verified
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#202647', fontWeight: 500 }}>
                    {formData.accountName}
                  </Typography>
                </Card>
              </Grid>
            </Collapse>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Account Name"
                value={formData.accountName}
                onChange={handleInputChange('accountName')}
                placeholder="John Doe"
                error={!!validationErrors.accountName}
                helperText={validationErrors.accountName}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Bank Code"
                value={formData.bankCode}
                disabled
                helperText="Automatically set based on selected bank"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || isSubmitting}
          sx={{
            background: isFormValid
              ? 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)'
              : undefined
          }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={18} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            'Add Bank Account'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AddCardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { hostedCheckoutLoading, hostedCheckoutUrl, hostedCheckoutReference, error } = useSelector((state: RootState) => state.cards);
  const [isVerifying, setIsVerifying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      dispatch(clearCardAddFlow());
    };
  }, [dispatch]);

  const handleInitialize = async () => {
    setLocalError(null);
    setSuccess(false);
    try {
      const result = await dispatch(initializeCardLink(undefined)).unwrap();
      const authUrl = result.authorization_url;
      const ref = result.reference;

      if (!authUrl) {
        setLocalError('Failed to initialise card linking. Please try again.');
        return;
      }

      // Open Paystack checkout in a popup window
      const width = 500;
      const height = 650;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        authUrl,
        'PaystackCheckout',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      if (!popup) {
        // Popup blocked — fall back to redirect
        window.location.href = authUrl;
        return;
      }

      popupRef.current = popup;

      // Poll for popup closure or callback redirect
      pollIntervalRef.current = setInterval(() => {
        try {
          // Check if popup was closed
          if (popup.closed) {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            // Try to verify — the user may have completed the flow
            handleVerify(ref);
            return;
          }

          // Check if popup navigated to callback URL with our reference
          try {
            const popupUrl = popup.location.href;
            if (popupUrl) {
              const url = new URL(popupUrl);
              const refParam = url.searchParams.get('reference') || url.searchParams.get('trxref');
              if (refParam === ref) {
                popup.close();
                if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
                handleVerify(ref);
              }
            }
          } catch {
            // Cross-origin — popup is still on Paystack's domain, keep polling
          }
        } catch {
          // Popup access error — keep polling
        }
      }, 500);
    } catch (err: any) {
      setLocalError(err || 'Failed to initialise card linking.');
    }
  };

  const handleVerify = async (reference: string) => {
    setIsVerifying(true);
    setLocalError(null);
    try {
      await dispatch(verifyCardLink(reference)).unwrap();
      setSuccess(true);
      // Navigate back to cards page after a brief delay
      setTimeout(() => navigate('/cards'), 2000);
    } catch (err: any) {
      setLocalError(err || 'Card linking could not be verified. If you completed the payment, your card will be linked automatically.');
    } finally {
      setIsVerifying(false);
    }
  };

  const displayError = localError || error;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#202647', mb: 3, fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>
        Link New Card
      </Typography>
      <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 4, maxWidth: 520, mx: 'auto' }}>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircle sx={{ fontSize: 64, color: '#00C48C', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202647', mb: 1 }}>
              Card Linked Successfully
            </Typography>
            <Typography variant="body2" sx={{ color: '#565972' }}>
              Redirecting to your cards...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Lock sx={{ fontSize: 48, color: '#11C1EC', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202647', mb: 1 }}>
              Secure Card Linking
            </Typography>
            <Typography variant="body2" sx={{ color: '#565972', mb: 3, lineHeight: 1.7 }}>
              You'll be redirected to Paystack's secure checkout page to enter your card details.
              MalPay never sees your card number, CVV, or PIN.
            </Typography>

            <Box sx={{ bgcolor: '#f0f9ff', borderRadius: 2, p: 2, mb: 3 }}>
              <Typography variant="body2" sx={{ color: '#565972' }}>
                A one-time fee of <strong>&#8358;50</strong> will be charged to verify your card.
              </Typography>
            </Box>

            {displayError && (
              <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                {displayError}
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={hostedCheckoutLoading || isVerifying}
              onClick={handleInitialize}
              startIcon={hostedCheckoutLoading || isVerifying ? <CircularProgress size={20} color="inherit" /> : <Security />}
              sx={{
                bgcolor: '#11C1EC',
                '&:hover': { bgcolor: '#0A7FB5' },
                borderRadius: 2,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
              }}
            >
              {hostedCheckoutLoading ? 'Preparing Checkout...' : isVerifying ? 'Verifying Card...' : 'Link Card via Paystack'}
            </Button>

            <Button
              variant="text"
              sx={{ mt: 2, color: '#565972' }}
              onClick={() => navigate('/cards')}
              disabled={hostedCheckoutLoading || isVerifying}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

const SendMoneyPage: React.FC = () => {
  const idemKeyRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  // Steps: 0=details, 1=confirm, 2=pin, 3=otp, 4=success
  const [step, setStep] = useState(0);
  const [pinStatus, setPinStatus] = useState<{ isPinSet: boolean; isLocked: boolean } | null>(null);
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [pin, setPin] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [successReference, setSuccessReference] = useState('');

  const [formData, setFormData] = useState({
    recipientEmail: '',
    recipientPhone: '',
    amount: '',
    settlementCurrency: 'NGN',
    description: '',
  });
  const [recipient, setRecipient] = useState<{ email?: string; phoneNumber?: string; name?: string } | null>(null);
  const [loadingRecipient, setLoadingRecipient] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fee calculator: Paystack 1.5% + ₦100 (if >= ₦2,500) capped ₦2,000 | MalPay 1% + ₦20 (if >= ₦2,500) capped ₦3,000
  const calculateTransferFees = (amt: number) => {
    if (amt <= 0) return { paystackFee: 0, malpayCharge: 0, totalFees: 0, totalDebit: 0 };
    let paystackFee = amt * 0.015 + (amt >= 2500 ? 100 : 0);
    paystackFee = Math.min(paystackFee, 2000);
    let malpayCharge = amt * 0.01 + (amt >= 2500 ? 20 : 0);
    malpayCharge = Math.min(malpayCharge, 3000);
    const totalFees = paystackFee + malpayCharge;
    return { paystackFee, malpayCharge, totalFees, totalDebit: amt + totalFees };
  };

  // Fetch PIN status on mount
  useEffect(() => {
    const fetchPinStatus = async () => {
      try {
        const res = await transferPinAPI.getStatus();
        setPinStatus(res.data.data || res.data);
      } catch { setPinStatus({ isPinSet: false, isLocked: false }); }
    };
    fetchPinStatus();
  }, []);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    setError(null);
  };

  const handleResolveRecipient = async () => {
    setError(null); setRecipient(null);
    if (!formData.recipientEmail && !formData.recipientPhone) { setError('Enter recipient email or phone number'); return; }
    setLoadingRecipient(true);
    try {
      const response = await usersAPI.resolvePublicUser({ email: formData.recipientEmail || undefined, phoneNumber: formData.recipientPhone || undefined });
      const data = response.data.data;
      setRecipient({ email: data.email, phoneNumber: data.phone_number || data.phoneNumber, name: `${data.first_name || ''} ${data.last_name || ''}`.trim() });
    } catch (err: any) { setError(err.response?.data?.error?.message || 'Failed to resolve recipient'); }
    finally { setLoadingRecipient(false); }
  };

  const handleNextToConfirm = () => {
    setError(null);
    if (!formData.recipientEmail && !formData.recipientPhone) { setError('Recipient email or phone is required'); return; }
    if (!formData.amount || Number(formData.amount) <= 0) { setError('Enter a valid amount'); return; }
    if (!recipient) { setError('Please resolve recipient first'); return; }
    setStep(1);
  };

  const handleNextToPin = () => { setStep(2); setPin(''); setNewPin(''); setConfirmNewPin(''); };

  const handlePinSubmit = async () => {
    setError(null);
    if (!pinStatus?.isPinSet) {
      // Setting new PIN
      if (newPin.length < 4) { setError('PIN must be at least 4 digits'); return; }
      if (newPin !== confirmNewPin) { setError('PINs do not match'); return; }
      setSubmitting(true);
      try {
        await transferPinAPI.setPin(newPin);
        setPinStatus({ isPinSet: true, isLocked: false });
        setPin(newPin);
        // Now send OTP
        setOtpSending(true);
        await transferPinAPI.sendTransferOTP();
        setOtpSent(true);
        setStep(3);
      } catch (err: any) { setError(err.response?.data?.error?.message || 'Failed to set PIN'); }
      finally { setSubmitting(false); setOtpSending(false); }
    } else {
      // Verify existing PIN
      if (pin.length < 4) { setError('Enter your transfer PIN'); return; }
      setSubmitting(true);
      try {
        await transferPinAPI.verifyPin(pin);
        // Send OTP
        setOtpSending(true);
        await transferPinAPI.sendTransferOTP();
        setOtpSent(true);
        setStep(3);
      } catch (err: any) { setError(err.response?.data?.error?.message || 'Invalid PIN'); }
      finally { setSubmitting(false); setOtpSending(false); }
    }
  };

  const handleTransfer = async () => {
    setError(null);
    if (!otp || otp.length < 6) { setError('Enter the 6-digit OTP sent to your email'); return; }
    setSubmitting(true);
    if (!idemKeyRef.current) idemKeyRef.current = newIdempotencyKey();
    try {
      const response = await paymentsAPI.transfer({
        recipientEmail: formData.recipientEmail || undefined,
        recipientPhone: formData.recipientPhone || undefined,
        amount: Number(formData.amount),
        currency: 'NGN',
        settlementCurrency: formData.settlementCurrency === 'USD' ? 'USD' : 'NGN',
        description: formData.description || undefined,
        pin: pinStatus?.isPinSet ? pin || newPin : newPin,
        otp,
      }, idemKeyRef.current);
      idemKeyRef.current = null;
      setSuccessReference(response.data.reference || response.data.data?.reference || '');
      setStep(4);
      dispatch(fetchTransactions());
    } catch (err: any) {
      // A definitive answer from the server ends this attempt; a network failure
      // keeps the key so a retry cannot charge twice.
      if (err.response) idemKeyRef.current = null;
      setError(err.response?.data?.error?.message || (err.response ? 'Transfer failed' : 'Network error. Please retry — your transfer will not be duplicated.'));
    }
    finally { setSubmitting(false); }
  };

  const handleReset = () => {
    setStep(0); setPin(''); setOtp(''); setNewPin(''); setConfirmNewPin('');
    setOtpSent(false); setSuccessReference(''); setSubmitting(false);
    setFormData({ recipientEmail: '', recipientPhone: '', amount: '', settlementCurrency: 'NGN', description: '' });
    setRecipient(null); setError(null);
  };

  const stepLabels = ['Details', 'Confirm', 'PIN', 'OTP', 'Done'];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton size="small" onClick={() => step === 0 ? navigate('/dashboard') : (step < 4 ? setStep(step - 1) : handleReset())}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#202647', fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>
          Send Money
        </Typography>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
        {stepLabels.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Step 0: Enter Details */}
        {step === 0 && (
          <Grid container spacing={3}>
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#202647', mb: 2 }}>Recipient</Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField fullWidth label="Recipient Email" value={formData.recipientEmail} onChange={handleInputChange('recipientEmail')} placeholder="recipient@example.com"
                    InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: '#7F839C', mr: 1 }} /></InputAdornment> }} />
                </Grid>
                <Grid size={12}>
                  <TextField fullWidth label="or Recipient Phone" value={formData.recipientPhone} onChange={handleInputChange('recipientPhone')} placeholder="+2348012345678" />
                </Grid>
                <Grid size={12}>
                  <Button variant="outlined" onClick={handleResolveRecipient} disabled={loadingRecipient} fullWidth sx={{ py: 1.2 }}>
                    {loadingRecipient ? <><CircularProgress size={18} sx={{ mr: 1 }} /> Finding Recipient...</> : 'Find Recipient'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            {recipient && (
              <Grid size={12}>
                <Card sx={{ p: 2.5, background: 'linear-gradient(135deg, rgba(0,196,140,0.05), rgba(17,193,236,0.05))', border: '1px solid #E8EBF0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 18 }}>
                      {(recipient.name?.[0] || 'U').toUpperCase()}
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202647' }}>{recipient.name || 'User'}</Typography>
                      <Typography variant="caption" sx={{ color: '#7F839C' }}>{recipient.email || recipient.phoneNumber}</Typography>
                    </Box>
                    <CheckCircle sx={{ ml: 'auto', color: '#00C48C' }} />
                  </Box>
                </Card>
              </Grid>
            )}
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#202647', mb: 2 }}>Amount</Typography>
              <TextField fullWidth label="Amount (NGN)" value={formData.amount} onChange={handleInputChange('amount')} placeholder="0.00" type="number"
                InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ color: '#7F839C', fontWeight: 600 }}>₦</Typography></InputAdornment> }} inputProps={{ step: '0.01' }} />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Settlement Currency</InputLabel>
                <Select value={formData.settlementCurrency} onChange={(e) => setFormData({ ...formData, settlementCurrency: e.target.value })} label="Settlement Currency">
                  <MenuItem value="NGN">NGN (Default)</MenuItem>
                  <MenuItem value="USD">USD Settlement</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Description (optional)" value={formData.description} onChange={handleInputChange('description')} placeholder="Payment for services" multiline rows={2} />
            </Grid>
            <Grid size={12}>
              <Button variant="contained" onClick={handleNextToConfirm} disabled={!recipient || !formData.amount || !user} fullWidth
                sx={{ py: 1.5, background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)', fontWeight: 600 }}>
                Next
              </Button>
            </Grid>
          </Grid>
        )}

        {/* Step 1: Confirm */}
        {step === 1 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647', mb: 3, textAlign: 'center' }}>Confirm Transfer</Typography>
            <Box sx={{ p: 2, bgcolor: 'rgba(17,193,236,0.05)', borderRadius: 2, mb: 2 }}>
              <Typography variant="caption" sx={{ color: '#7F839C' }}>Recipient</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 16 }}>
                  {(recipient?.name?.[0] || 'U').toUpperCase()}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#202647' }}>{recipient?.name || 'User'}</Typography>
                  <Typography variant="caption" sx={{ color: '#7F839C' }}>{recipient?.email || recipient?.phoneNumber}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ p: 2, bgcolor: 'rgba(0,196,140,0.05)', borderRadius: 2, mb: 2 }}>
              <Typography variant="caption" sx={{ color: '#7F839C' }}>Amount</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#00C48C', mt: 0.5 }}>₦{Number(formData.amount).toLocaleString('en-NG', { minimumFractionDigits: 2 })}</Typography>
            </Box>
            {/* Fee Breakdown */}
            {formData.amount && (() => {
              const fees = calculateTransferFees(Number(formData.amount));
              return (
                <Box sx={{ p: 2, bgcolor: '#F8F8FC', borderRadius: 2, mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#7F839C', mb: 1, display: 'block' }}>Fee Breakdown</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#7F839C' }}>Paystack Fee</Typography>
                    <Typography variant="body2" sx={{ color: '#202647' }}>₦{fees.paystackFee.toLocaleString('en-NG', { minimumFractionDigits: 2 })}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#7F839C' }}>MalPay Charge</Typography>
                    <Typography variant="body2" sx={{ color: '#202647' }}>₦{fees.malpayCharge.toLocaleString('en-NG', { minimumFractionDigits: 2 })}</Typography>
                  </Box>
                  <Box sx={{ borderTop: '1px solid #E8EBF0', pt: 1, mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#202647' }}>Total Debit</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#11C1EC' }}>₦{fees.totalDebit.toLocaleString('en-NG', { minimumFractionDigits: 2 })}</Typography>
                  </Box>
                </Box>
              );
            })()}
            {formData.description && (
              <Box sx={{ p: 2, bgcolor: '#F8F8FC', borderRadius: 2, mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#7F839C' }}>Description</Typography>
                <Typography variant="body2" sx={{ color: '#202647', mt: 0.5 }}>{formData.description}</Typography>
              </Box>
            )}
            <Alert severity="info" sx={{ mb: 3 }}>Once sent, this transfer cannot be reversed.</Alert>
            <Button variant="contained" onClick={handleNextToPin} fullWidth sx={{ py: 1.5, background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)', fontWeight: 600 }}>
              Continue
            </Button>
          </Box>
        )}

        {/* Step 2: PIN */}
        {step === 2 && (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(17,193,236,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <Lock sx={{ color: '#11C1EC', fontSize: 32 }} />
            </Box>
            {!pinStatus?.isPinSet ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647', mb: 1 }}>Set Your Transfer PIN</Typography>
                <Typography variant="body2" sx={{ color: '#7F839C', mb: 3 }}>Create a 4-6 digit PIN to secure your transfers.</Typography>
                <TextField fullWidth type="password" label="New PIN" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                  inputProps={{ maxLength: 6, inputMode: 'numeric' }} sx={{ mb: 2 }} />
                <TextField fullWidth type="password" label="Confirm PIN" value={confirmNewPin} onChange={(e) => setConfirmNewPin(e.target.value.replace(/\D/g, ''))}
                  inputProps={{ maxLength: 6, inputMode: 'numeric' }} sx={{ mb: 3 }} />
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647', mb: 1 }}>Enter Transfer PIN</Typography>
                <Typography variant="body2" sx={{ color: '#7F839C', mb: 3 }}>Enter your 4-6 digit transfer PIN to continue.</Typography>
                <TextField fullWidth type="password" label="Transfer PIN" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  inputProps={{ maxLength: 6, inputMode: 'numeric' }} sx={{ mb: 3 }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handlePinSubmit(); }} />
              </>
            )}
            <Button variant="contained" onClick={handlePinSubmit} disabled={submitting} fullWidth
              sx={{ py: 1.5, background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)', fontWeight: 600 }}>
              {submitting ? <><CircularProgress size={18} sx={{ mr: 1, color: 'white' }} /> Processing...</> : 'Continue'}
            </Button>
          </Box>
        )}

        {/* Step 3: OTP */}
        {step === 3 && (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,196,140,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <Security sx={{ color: '#00C48C', fontSize: 32 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647', mb: 1 }}>Enter OTP</Typography>
            <Typography variant="body2" sx={{ color: '#7F839C', mb: 3 }}>A 6-digit code has been sent to your email. Enter it below to confirm the transfer.</Typography>
            <TextField fullWidth label="OTP Code" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              inputProps={{ maxLength: 6, inputMode: 'numeric' }} sx={{ mb: 3 }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleTransfer(); }} />
            <Button variant="contained" onClick={handleTransfer} disabled={submitting || otp.length < 6} fullWidth
              sx={{ py: 1.5, background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)', fontWeight: 600, mb: 2 }}>
              {submitting ? <><CircularProgress size={18} sx={{ mr: 1, color: 'white' }} /> Sending...</> : 'Confirm Transfer'}
            </Button>
            <Button variant="text" onClick={async () => { setOtpSending(true); try { await transferPinAPI.sendTransferOTP(); } catch {} finally { setOtpSending(false); } }}
              disabled={otpSending} sx={{ color: '#11C1EC', textTransform: 'none' }}>
              {otpSending ? 'Resending...' : 'Resend OTP'}
            </Button>
          </Box>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #00C48C 0%, #009A6E 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
              <CheckCircle sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#202647', mb: 1 }}>Transfer Successful!</Typography>
            <Typography variant="body2" sx={{ color: '#7F839C', mb: 3 }}>Your transfer has been sent successfully.</Typography>
            {successReference && (
              <Box sx={{ p: 2, bgcolor: '#F8F8FC', borderRadius: 1, mb: 3 }}>
                <Typography variant="caption" sx={{ color: '#7F839C' }}>Reference ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#202647', mt: 0.5 }}>{successReference}</Typography>
              </Box>
            )}
            <Button variant="contained" onClick={handleReset} fullWidth sx={{ py: 1.5, background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)', fontWeight: 600, mb: 1 }}>
              Send Another
            </Button>
            <Button variant="text" onClick={() => navigate('/dashboard')} sx={{ color: '#11C1EC', textTransform: 'none' }}>Back to Dashboard</Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

const TransactionHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { transactions, isLoading, error, pagination } = useSelector((state: RootState) => state.transactions);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const apiType =
      typeFilter === 'send' ? 'transfer' : typeFilter === 'receive' ? 'receive' : undefined;
    const apiStatus = statusFilter === 'all' ? undefined : statusFilter;
    dispatch(fetchTransactions({
      page,
      limit: pageSize,
      type: apiType,
      status: apiStatus,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }));
  }, [dispatch, page, pageSize, statusFilter, typeFilter, startDate, endDate]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, typeFilter, startDate, endDate]);

  const totalPages = Math.max(1, pagination.totalPages || 1);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#202647', mb: 3, fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>
        Transaction History
      </Typography>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 , borderRadius: 4 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Successful</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="send">Send</MenuItem>
                <MenuItem value="receive">Receive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 , borderRadius: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {isLoading && <Typography>Loading transactions...</Typography>}
        {!isLoading && transactions.length === 0 && (
          <Typography>No transactions found.</Typography>
        )}
        {!isLoading && transactions.length > 0 && (
          <List>
            {transactions.map((tx: any) => (
              <ListItem
                key={tx.id}
                divider
                onClick={() => navigate(`/transaction/${tx.id}`)}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemText
                  primary={`${tx.type.replace('_', ' ')} • ₦${Number(tx.amount).toLocaleString()}`}
                  secondary={`Status: ${tx.status} • ${new Date(tx.created_at || tx.createdAt).toLocaleString()}`}
                  sx={{ minWidth: 0 }}
                />
                <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
                  <Chip
                    label={tx.type === 'receive' ? 'Receive' : 'Send'}
                    size="small"
                    sx={{
                      mb: 1,
                      bgcolor: tx.type === 'receive' ? 'rgba(0,196,140,0.1)' : 'rgba(17,193,236,0.1)',
                      color: tx.type === 'receive' ? '#00C48C' : '#11C1EC',
                      fontWeight: 'bold',
                    }}
                  />
                  {tx.total_fees || tx.totalFees ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                      Fees: ₦{Number(tx.total_fees || tx.totalFees).toLocaleString()}
                    </Typography>
                  ) : null}
                  {tx.failure_reason || tx.failureReason ? (
                    <Typography variant="body2" color="error">
                      {tx.failure_reason || tx.failureReason}
                    </Typography>
                  ) : null}
                </Box>
              </ListItem>
            ))}
          </List>
        )}
        {!isLoading && transactions.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

const BankAccountsPage: React.FC = () => {
  const [addBankOpen, setAddBankOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { bankAccounts, addingAccount, error } = useSelector((state: RootState) => state.bankAccounts);

  const handleAddBank = () => {
    setAddBankOpen(true);
  };

  const handleCloseAddBank = () => {
    setAddBankOpen(false);
  };

  useEffect(() => {
    dispatch(fetchBankAccounts());
  }, [dispatch]);

  const handleSubmitBank = async (bankData: any) => {
    const result = await dispatch(addBankAccount(bankData));
    if (addBankAccount.fulfilled.match(result)) {
      setAddBankOpen(false);
    }
  };

  const handleSetDefaultBank = async (accountId: string) => {
    await dispatch(setDefaultBankAccount(accountId));
  };

  const handleDeleteBank = async (accountId: string) => {
    if (window.confirm('Are you sure you want to remove this bank account?')) {
      await dispatch(deleteBankAccount(accountId));
    }
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Mobile App Bar */}
      <Box sx={{
        bgcolor: 'white',
        px: 2,
        py: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: 1
      }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#202647' }}>
          Bank Accounts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddBank}
          sx={{ borderRadius: 3 }}
        >
          Add Account
        </Button>
      </Box>

      <Box sx={{ p: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {bankAccounts.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>
              No bank accounts added yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add your first bank account to start receiving payments
            </Typography>
            <Button variant="contained" onClick={handleAddBank} sx={{ borderRadius: 3 }}>
              Add Your First Bank Account
            </Button>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {bankAccounts.map((account) => (
              <Grid size={12} key={account.id}>
                <Card
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 3,
                    background: (account as any).isDefault
                      ? 'linear-gradient(135deg, #006B4F 0%, #00895F 50%, #00C48C 100%)'
                      : 'linear-gradient(135deg, #09486D 0%, #0a5d8a 50%, #11C1EC 100%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    maxWidth: 480,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-30%',
                      right: '-20%',
                      width: '60%',
                      height: '80%',
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, textShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>
                      {account.bankName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {(account as any).isDefault && (
                        <Chip
                          label="Default"
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            backdropFilter: 'blur(4px)',
                          }}
                        />
                      )}
                      {account.isVerified && (
                        <Chip
                          label="Verified"
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.15)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            backdropFilter: 'blur(4px)',
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Typography sx={{ mb: 2.5, fontFamily: '"Courier New", monospace', fontWeight: 'bold', fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.8rem' }, letterSpacing: '0.1em', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                    {account.accountNumber}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.1em', display: 'block', mb: 0.25 }}>
                        Account Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                        {account.accountName}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.1em', display: 'block', mb: 0.25 }}>
                        Bank Code
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                        {account.bankCode}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                    {!(account as any).isDefault && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSetDefaultBank(account.id)}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.15)',
                          color: 'white',
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          backdropFilter: 'blur(4px)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        Set Default
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleDeleteBank(account.id)}
                      sx={{
                        bgcolor: 'rgba(239,68,68,0.3)',
                        color: '#fca5a5',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        backdropFilter: 'blur(4px)',
                        '&:hover': { bgcolor: 'rgba(239,68,68,0.5)' },
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <AddBankDialog
          open={addBankOpen}
          onClose={handleCloseAddBank}
          onSubmit={handleSubmitBank}
          isSubmitting={addingAccount}
        />
      </Box>
    </Box>
  );
};

const AddBankAccountPage: React.FC = () => {
  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#202647', mb: 3, fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>
        Add Bank Account
      </Typography>
      <Paper sx={{ p: { xs: 2.5, sm: 4 }, textAlign: 'center' , borderRadius: 4 }}>
        <Typography variant="body1">
          Add bank account functionality will be implemented here.
        </Typography>
      </Paper>
    </Box>
  );
};

const ProfilePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phoneNumber: user?.phoneNumber || '',
  });

  useEffect(() => {
    setForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: user?.phoneNumber || '',
    });
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    setProfileError(null);
    setProfileSuccess(null);
    try {
      await usersAPI.updateProfile(form);
      setProfileSuccess('Profile updated successfully');
      setEditing(false);
    } catch (err: any) {
      setProfileError(err.response?.data?.error?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setProfileError('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    setProfileError(null);
    try {
      const response = await usersAPI.uploadProfileImage(file);
      if (response.data?.data?.profileImage) {
        dispatch(updateUserField({ profileImage: response.data.data.profileImage }));
      }
      setProfileSuccess('Profile image updated successfully');
    } catch (err: any) {
      setProfileError(err.response?.data?.error?.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const userInitials = `${user?.firstName?.[0]?.toUpperCase() || ''}${user?.lastName?.[0]?.toUpperCase() || ''}` || 'U';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 700, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#202647', mb: 3, fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>
        Profile
      </Typography>

      {profileSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setProfileSuccess(null)}>{profileSuccess}</Alert>}
      {profileError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProfileError(null)}>{profileError}</Alert>}

      <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 }, mb: 4, flexWrap: 'wrap' }}>
          <Box sx={{ position: 'relative' }}>
            <MuiAvatar
              src={user?.profileImage || undefined}
              sx={{
                width: { xs: 60, sm: 80 }, height: { xs: 60, sm: 80 },
                background: 'linear-gradient(135deg, #11C1EC, #09486D)',
                fontSize: { xs: 24, sm: 32 }, fontWeight: 700,
              }}
            >
              {userInitials}
            </MuiAvatar>
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              sx={{
                position: 'absolute', bottom: -4, right: -4,
                bgcolor: '#11C1EC', color: 'white',
                width: 28, height: 28,
                '&:hover': { bgcolor: '#09486D' },
              }}
            >
              {uploadingImage ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <CameraAltIcon sx={{ fontSize: 16 }} />}
            </IconButton>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              style={{ display: 'none' }}
              onChange={handleImageUpload}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#202647', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#7F839C', wordBreak: 'break-all' }}>
              {user?.email}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth label="First Name" value={form.firstName}
              onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))}
              disabled={!editing} variant="outlined"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth label="Last Name" value={form.lastName}
              onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))}
              disabled={!editing} variant="outlined"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth label="Email Address" value={user?.email || ''}
              disabled variant="outlined"
              helperText="Email cannot be changed"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth label="Phone Number" value={form.phoneNumber}
              onChange={e => setForm(p => ({ ...p, phoneNumber: e.target.value }))}
              disabled={!editing} variant="outlined"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {editing ? (
            <>
              <Button variant="outlined" onClick={() => { setEditing(false); setForm({ firstName: user?.firstName || '', lastName: user?.lastName || '', phoneNumber: user?.phoneNumber || '' }); }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}
                sx={{ background: 'linear-gradient(135deg, #11C1EC, #09486D)' }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setEditing(true)}
              sx={{ background: 'linear-gradient(135deg, #11C1EC, #09486D)' }}>
              Edit Profile
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 4, borderRadius: 4, mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647', mb: 2 }}>
          Account Information
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid #F0F0F6' }}>
          <Typography variant="body2" sx={{ color: '#7F839C' }}>Member Since</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#202647' }}>
            {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid #F0F0F6' }}>
          <Typography variant="body2" sx={{ color: '#7F839C' }}>Email Verified</Typography>
          <Chip label={user?.isEmailVerified ? 'Verified' : 'Not Verified'} size="small"
            sx={{ bgcolor: user?.isEmailVerified ? '#E8F5E9' : '#FFF3E0', color: user?.isEmailVerified ? '#2E7D32' : '#E65100' }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5 }}>
          <Typography variant="body2" sx={{ color: '#7F839C' }}>Account Status</Typography>
          <Chip label="Active" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32' }} />
        </Box>
      </Paper>
    </Box>
  );
};

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  // Transfer PIN state
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinMode, setPinMode] = useState<'set' | 'change' | 'forgot'>('set');
  const [pinStep, setPinStep] = useState(0); // 0=form, 1=otp (forgot only), 2=success
  const [pinStatus, setPinStatus] = useState<{ isPinSet: boolean; isLocked: boolean; lockExpiresAt: string | null } | null>(null);
  const [pinLoading, setPinLoading] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinSuccessMsg, setPinSuccessMsg] = useState<string | null>(null);
  const [pinForm, setPinForm] = useState({ currentPin: '', newPin: '', confirmPin: '', otp: '' });

  // Fetch PIN status on mount
  useEffect(() => {
    const fetchPinStatus = async () => {
      try {
        const res = await transferPinAPI.getStatus();
        setPinStatus(res.data.data || res.data);
      } catch (err) {
        // Silently fail - user may not have PIN set
      }
    };
    fetchPinStatus();
  }, []);

  const openPinDialog = () => {
    setPinError(null);
    setPinSuccessMsg(null);
    setPinForm({ currentPin: '', newPin: '', confirmPin: '', otp: '' });
    setPinStep(0);
    if (pinStatus?.isPinSet) {
      setPinMode('change');
    } else {
      setPinMode('set');
    }
    setPinDialogOpen(true);
  };

  const handleSetPin = async () => {
    setPinError(null);
    if (pinForm.newPin.length < 4 || pinForm.newPin.length > 6 || !/^\d+$/.test(pinForm.newPin)) {
      setPinError('PIN must be 4-6 digits'); return;
    }
    if (pinForm.newPin !== pinForm.confirmPin) {
      setPinError('PINs do not match'); return;
    }
    setPinLoading(true);
    try {
      await transferPinAPI.setPin(pinForm.newPin);
      setPinStatus({ isPinSet: true, isLocked: false, lockExpiresAt: null });
      setPinSuccessMsg('Transfer PIN set successfully!');
      setPinStep(2);
    } catch (err: any) {
      setPinError(err.response?.data?.error?.message || 'Failed to set PIN');
    } finally {
      setPinLoading(false);
    }
  };

  const handleChangePin = async () => {
    setPinError(null);
    if (!pinForm.currentPin) {
      setPinError('Enter your current PIN'); return;
    }
    if (pinForm.newPin.length < 4 || pinForm.newPin.length > 6 || !/^\d+$/.test(pinForm.newPin)) {
      setPinError('New PIN must be 4-6 digits'); return;
    }
    if (pinForm.newPin !== pinForm.confirmPin) {
      setPinError('New PINs do not match'); return;
    }
    setPinLoading(true);
    try {
      await transferPinAPI.changePin(pinForm.currentPin, pinForm.newPin);
      setPinSuccessMsg('Transfer PIN changed successfully!');
      setPinStep(2);
    } catch (err: any) {
      setPinError(err.response?.data?.error?.message || 'Failed to change PIN');
    } finally {
      setPinLoading(false);
    }
  };

  const handleForgotPin = async () => {
    setPinError(null);
    setPinLoading(true);
    try {
      await transferPinAPI.forgotPin();
      setPinMode('forgot');
      setPinStep(1); // Go to OTP step
    } catch (err: any) {
      setPinError(err.response?.data?.error?.message || 'Failed to send OTP');
    } finally {
      setPinLoading(false);
    }
  };

  const handleResetPinWithOtp = async () => {
    setPinError(null);
    if (!pinForm.otp || pinForm.otp.length !== 6) {
      setPinError('Enter a valid 6-digit OTP'); return;
    }
    if (pinForm.newPin.length < 4 || pinForm.newPin.length > 6 || !/^\d+$/.test(pinForm.newPin)) {
      setPinError('New PIN must be 4-6 digits'); return;
    }
    if (pinForm.newPin !== pinForm.confirmPin) {
      setPinError('New PINs do not match'); return;
    }
    setPinLoading(true);
    try {
      await transferPinAPI.resetPin(pinForm.otp, pinForm.newPin);
      setPinSuccessMsg('Transfer PIN reset successfully!');
      setPinStep(2);
    } catch (err: any) {
      setPinError(err.response?.data?.error?.message || 'Failed to reset PIN');
    } finally {
      setPinLoading(false);
    }
  };

  const closePinDialog = () => {
    setPinDialogOpen(false);
    setPinError(null);
    setPinForm({ currentPin: '', newPin: '', confirmPin: '', otp: '' });
    setPinStep(0);
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    setPasswordSaving(true);
    try {
      await usersAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordSuccess('Password changed successfully');
      setChangePasswordOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setPasswordError(err.response?.data?.error?.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const settingsGroups = [
    {
      title: 'Account',
      items: [
        { label: 'Profile', description: 'Manage your personal information', icon: <AccountCircle sx={{ color: '#11C1EC' }} />, action: () => navigate('/profile') },
        { label: 'Linked Cards', description: 'Manage your payment cards', icon: <CreditCard sx={{ color: '#11C1EC' }} />, action: () => navigate('/cards') },
        { label: 'Bank Accounts', description: 'Manage withdrawal accounts', icon: <AccountBalance sx={{ color: '#11C1EC' }} />, action: () => navigate('/bank-accounts') },
      ],
    },
    {
      title: 'Security',
      items: [
        { label: 'Change Password', description: 'Update your account password', icon: <Lock sx={{ color: '#FF9800' }} />, action: () => setChangePasswordOpen(true) },
        {
          label: 'Transfer PIN',
          description: pinStatus?.isPinSet ? 'Change or reset your transfer PIN' : 'Set up your transfer PIN',
          icon: <Security sx={{ color: '#FF9800' }} />,
          action: openPinDialog,
        },
      ],
    },
    {
      title: 'Support',
      items: [
        { label: 'Help Center', description: 'Get help with MalPay', icon: <Person sx={{ color: '#7C3AED' }} />, action: () => {} },
      ],
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 700, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#202647', mb: 3, fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>
        Settings
      </Typography>

      {passwordSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPasswordSuccess(null)}>{passwordSuccess}</Alert>}
      {pinSuccessMsg && !pinDialogOpen && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPinSuccessMsg(null)}>{pinSuccessMsg}</Alert>}

      {settingsGroups.map((group) => (
        <Paper key={group.title} sx={{ p: 0, borderRadius: 4, mb: 3, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, bgcolor: '#F8F8FC', borderBottom: '1px solid #F0F0F6' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#202647' }}>
              {group.title}
            </Typography>
          </Box>
          <List disablePadding>
            {group.items.map((item, idx) => (
              <React.Fragment key={item.label}>
                <ListItem
                  sx={{ px: 3, py: 2, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(17,193,236,0.04)' } }}
                  onClick={item.action}
                >
                  <Box sx={{ mr: 2 }}>{item.icon}</Box>
                  <ListItemText
                    primary={item.label}
                    secondary={item.description}
                    primaryTypographyProps={{ fontWeight: 600, color: '#202647', fontSize: '0.95rem' }}
                    secondaryTypographyProps={{ color: '#7F839C', fontSize: '0.8rem' }}
                  />
                  <Typography sx={{ color: '#B0B3C5' }}>&rsaquo;</Typography>
                </ListItem>
                {idx < group.items.length - 1 && <Box sx={{ borderBottom: '1px solid #F0F0F6', mx: 3 }} />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      ))}

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Change Password</DialogTitle>
        <DialogContent>
          {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
          <TextField
            fullWidth label="Current Password" type="password" sx={{ mt: 1, mb: 2 }}
            value={passwordForm.currentPassword}
            onChange={e => setPasswordForm(p => ({ ...p, currentPassword: e.target.value }))}
          />
          <TextField
            fullWidth label="New Password" type="password" sx={{ mb: 2 }}
            value={passwordForm.newPassword}
            onChange={e => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))}
          />
          <TextField
            fullWidth label="Confirm New Password" type="password"
            value={passwordForm.confirmPassword}
            onChange={e => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword} disabled={passwordSaving}
            sx={{ background: 'linear-gradient(135deg, #11C1EC, #09486D)' }}>
            {passwordSaving ? 'Saving...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer PIN Dialog */}
      <Dialog open={pinDialogOpen} onClose={closePinDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {pinStep === 2 ? 'Success' : pinMode === 'set' ? 'Set Transfer PIN' : pinMode === 'forgot' ? 'Reset Transfer PIN' : 'Change Transfer PIN'}
        </DialogTitle>
        <DialogContent>
          {pinError && <Alert severity="error" sx={{ mb: 2 }}>{pinError}</Alert>}

          {/* SUCCESS STEP */}
          {pinStep === 2 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{
                width: 64, height: 64, borderRadius: '50%', bgcolor: '#E8F5E9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 2,
              }}>
                <Security sx={{ color: '#00C48C', fontSize: 32 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647', mb: 1 }}>
                {pinSuccessMsg || 'PIN updated successfully!'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#7F839C' }}>
                Your transfer PIN has been updated. You can use it for future transfers.
              </Typography>
            </Box>
          )}

          {/* SET PIN FORM (no existing pin) */}
          {pinStep === 0 && pinMode === 'set' && (
            <>
              <Typography variant="body2" sx={{ color: '#7F839C', mb: 2, mt: 1 }}>
                Create a 4-6 digit PIN to secure your transfers.
              </Typography>
              <TextField
                fullWidth label="New PIN" type="password" sx={{ mb: 2 }}
                value={pinForm.newPin} inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={e => setPinForm(p => ({ ...p, newPin: e.target.value.replace(/\D/g, '') }))}
              />
              <TextField
                fullWidth label="Confirm PIN" type="password"
                value={pinForm.confirmPin} inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={e => setPinForm(p => ({ ...p, confirmPin: e.target.value.replace(/\D/g, '') }))}
              />
            </>
          )}

          {/* CHANGE PIN FORM */}
          {pinStep === 0 && pinMode === 'change' && (
            <>
              <Typography variant="body2" sx={{ color: '#7F839C', mb: 2, mt: 1 }}>
                Enter your current PIN and choose a new one.
              </Typography>
              <TextField
                fullWidth label="Current PIN" type="password" sx={{ mb: 2 }}
                value={pinForm.currentPin} inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={e => setPinForm(p => ({ ...p, currentPin: e.target.value.replace(/\D/g, '') }))}
              />
              <TextField
                fullWidth label="New PIN" type="password" sx={{ mb: 2 }}
                value={pinForm.newPin} inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={e => setPinForm(p => ({ ...p, newPin: e.target.value.replace(/\D/g, '') }))}
              />
              <TextField
                fullWidth label="Confirm New PIN" type="password"
                value={pinForm.confirmPin} inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={e => setPinForm(p => ({ ...p, confirmPin: e.target.value.replace(/\D/g, '') }))}
              />
              <Button
                variant="text" size="small" sx={{ mt: 1, color: '#11C1EC', textTransform: 'none' }}
                onClick={handleForgotPin} disabled={pinLoading}
              >
                Forgot your PIN?
              </Button>
            </>
          )}

          {/* FORGOT PIN - OTP + NEW PIN STEP */}
          {pinStep === 1 && pinMode === 'forgot' && (
            <>
              <Typography variant="body2" sx={{ color: '#7F839C', mb: 2, mt: 1 }}>
                A 6-digit OTP has been sent to your email. Enter it below along with your new PIN.
              </Typography>
              <TextField
                fullWidth label="OTP Code" sx={{ mb: 2 }}
                value={pinForm.otp} inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={e => setPinForm(p => ({ ...p, otp: e.target.value.replace(/\D/g, '') }))}
                placeholder="6-digit code"
              />
              <TextField
                fullWidth label="New PIN" type="password" sx={{ mb: 2 }}
                value={pinForm.newPin} inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={e => setPinForm(p => ({ ...p, newPin: e.target.value.replace(/\D/g, '') }))}
              />
              <TextField
                fullWidth label="Confirm New PIN" type="password"
                value={pinForm.confirmPin} inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={e => setPinForm(p => ({ ...p, confirmPin: e.target.value.replace(/\D/g, '') }))}
              />
              <Button
                variant="text" size="small" sx={{ mt: 1, color: '#11C1EC', textTransform: 'none' }}
                onClick={handleForgotPin} disabled={pinLoading}
              >
                Resend OTP
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {pinStep === 2 ? (
            <Button variant="contained" onClick={closePinDialog}
              sx={{ background: 'linear-gradient(135deg, #11C1EC, #09486D)' }}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={closePinDialog}>Cancel</Button>
              <Button
                variant="contained" disabled={pinLoading}
                sx={{ background: 'linear-gradient(135deg, #11C1EC, #09486D)' }}
                onClick={() => {
                  if (pinMode === 'set') handleSetPin();
                  else if (pinMode === 'change' && pinStep === 0) handleChangePin();
                  else if (pinMode === 'forgot' && pinStep === 1) handleResetPinWithOtp();
                }}
              >
                {pinLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> :
                  pinMode === 'set' ? 'Set PIN' :
                  pinMode === 'forgot' ? 'Reset PIN' : 'Change PIN'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const QRCodePage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const payUrl = React.useMemo(() => {
    if (!user?.email) return '';
    const payload = btoa(JSON.stringify({
      email: user.email,
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
    }));
    return `${window.location.origin}/pay?to=${payload}`;
  }, [user]);

  useEffect(() => {
    const fetchQr = async () => {
      try {
        const response = await usersAPI.getQrCode();
        setQrCode(response.data.data.qrCode);
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to load QR code');
      }
    };
    fetchQr();
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(payUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Pay ${user?.firstName || 'me'} on MalPay`,
          text: `Send money to ${user?.firstName || 'me'} using MalPay`,
          url: payUrl,
        });
      } catch {}
    } else {
      handleCopyLink();
    }
  };

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User';
  const userInitials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || 'U';

  return (
    <Box sx={{ maxWidth: { xs: '100%', sm: 520 }, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#202647', mb: 3, fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>
        Your QR Code
      </Typography>

      {/* Main QR Card */}
      <Card sx={{
        borderRadius: 4,
        overflow: 'hidden',
        boxShadow: '0 8px 40px rgba(9, 72, 109, 0.12)',
        border: 'none',
      }}>
        {/* Header gradient */}
        <Box sx={{
          background: 'linear-gradient(135deg, #09486D 0%, #11C1EC 50%, #09486D 100%)',
          backgroundSize: '200% 200%',
          animation: 'gradientShift 8s ease infinite',
          px: 3, py: 3,
          display: 'flex', alignItems: 'center', gap: 2,
          position: 'relative', overflow: 'hidden',
          '@keyframes gradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
        }}>
          {/* Decorative circles */}
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <Box sx={{ position: 'absolute', bottom: -20, left: '40%', width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

          <Box sx={{
            width: 52, height: 52, borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.1rem', fontWeight: 800, color: 'white',
            backdropFilter: 'blur(8px)',
          }}>
            {userInitials}
          </Box>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.15rem' }}>{userName}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>{user?.email || ''}</Typography>
          </Box>
        </Box>

        {/* QR Code body */}
        <Box sx={{ p: { xs: 2.5, sm: 4 }, textAlign: 'center', bgcolor: 'white' }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!error && !qrCode && (
            <Box sx={{ py: 4 }}>
              <CircularProgress size={40} sx={{ color: '#11C1EC' }} />
              <Typography sx={{ mt: 2, color: '#7F839C' }}>Generating your QR code...</Typography>
            </Box>
          )}
          {qrCode && (
            <>
              <Box sx={{
                display: 'inline-block',
                p: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(17, 193, 236, 0.06) 0%, rgba(9, 72, 109, 0.04) 100%)',
                border: '2px solid rgba(17, 193, 236, 0.1)',
                mb: 2.5,
              }}>
                <Box component="img" src={qrCode} alt="MalPay QR Code" sx={{ width: { xs: 160, sm: 200 }, height: { xs: 160, sm: 200 }, display: 'block', borderRadius: 2 }} />
              </Box>

              <Typography sx={{ color: '#202647', fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
                Scan to pay {user?.firstName || 'me'}
              </Typography>
              <Typography sx={{ color: '#7F839C', fontSize: '0.85rem', mb: 3 }}>
                Anyone with MalPay can scan this code to send you money
              </Typography>

              {/* Action buttons */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 1.5 }, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleShare}
                  startIcon={<Send sx={{ fontSize: 18 }} />}
                  sx={{
                    background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)',
                    borderRadius: 3, px: 3, py: 1.2,
                    textTransform: 'none', fontWeight: 600,
                  }}
                >
                  Share
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCopyLink}
                  sx={{
                    borderRadius: 3, px: 3, py: 1.2,
                    borderColor: '#11C1EC', color: '#09486D',
                    textTransform: 'none', fontWeight: 600,
                    '&:hover': { borderColor: '#09486D', bgcolor: 'rgba(17, 193, 236, 0.05)' },
                  }}
                >
                  {copied ? 'Copied!' : 'Copy Link'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Card>

      {/* Info section */}
      <Box sx={{ mt: 3, p: 2.5, borderRadius: 3, background: 'rgba(17, 193, 236, 0.05)', border: '1px solid rgba(17, 193, 236, 0.1)' }}>
        <Typography sx={{ fontWeight: 600, color: '#09486D', fontSize: '0.9rem', mb: 1 }}>How it works</Typography>
        <Typography sx={{ color: '#565972', fontSize: '0.85rem', lineHeight: 1.7 }}>
          Share your QR code or payment link with anyone. When scanned, it opens a payment form pre-filled with your details so they can send you money instantly.
        </Typography>
      </Box>
    </Box>
  );
};

const NotFoundPage: React.FC = () => {
  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' , borderRadius: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#202647', mb: 3, fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>
          404 - Page Not Found
        </Typography>
        <Typography variant="body1">
          The page you're looking for doesn't exist.
        </Typography>
      </Paper>
    </Box>
  );
};

// KYC Page Component
const KYCPageComponent: React.FC = () => {
  const [selectedDocType, setSelectedDocType] = useState('passport');
  const [docNumber, setDocNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { documents, status, uploading, deleting, error: kycError } = useSelector((state: RootState) => state.kyc);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    dispatch(fetchKYCStatus());
    dispatch(fetchKYCDocuments());
  }, [dispatch]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !docNumber) {
      alert('Please select a file and enter document number');
      return;
    }

    const formData = new FormData();
    formData.append('documentType', selectedDocType);
    formData.append('documentNumber', docNumber);
    formData.append('document', selectedFile);
    if (expiryDate) {
      formData.append('expiryDate', expiryDate);
    }

    try {
      await dispatch(uploadKYCDocument(formData)).unwrap();
      setSuccessMessage('Document uploaded successfully!');
      setSelectedFile(null);
      setDocNumber('');
      setExpiryDate('');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  const handleDelete = async (documentId: string) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      await dispatch(deleteKYCDocument(documentId));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return '#00C48C';
      case 'rejected':
        return '#FF4757';
      case 'pending':
      default:
        return '#FFA500';
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#202647', mb: 3, fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>
        KYC Verification
      </Typography>

      {status && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Current Status</Typography>
          <Chip
            label={status.status.replace('_', ' ').toUpperCase()}
            sx={{
              bgcolor: status.status === 'verified' ? '#00C48C' : status.status === 'rejected' ? '#FF4757' : '#FFA500',
              color: 'white',
              fontWeight: 700,
            }}
          />
          {status.rejectionReason && (
            <Typography variant="body2" sx={{ mt: 2, color: '#FF4757' }}>
              Reason: {status.rejectionReason}
            </Typography>
          )}
        </Paper>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Upload Document</Typography>

            {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
            {kycError && <Alert severity="error" sx={{ mb: 2 }}>{kycError}</Alert>}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={selectedDocType}
                label="Document Type"
                onChange={(e) => setSelectedDocType(e.target.value)}
              >
                <MenuItem value="passport">Passport</MenuItem>
                <MenuItem value="national_id">National ID</MenuItem>
                <MenuItem value="drivers_license">Driver's License</MenuItem>
                <MenuItem value="utility_bill">Utility Bill</MenuItem>
                <MenuItem value="bank_statement">Bank Statement</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Document Number"
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
              sx={{ mb: 2 }}
            />

            {['passport', 'national_id', 'drivers_license'].includes(selectedDocType) && (
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            )}

            <Button
              variant="outlined"
              fullWidth
              component="label"
              sx={{ mb: 2, py: 1.5, borderColor: '#11C1EC', color: '#11C1EC' }}
            >
              Choose File
              <input type="file" hidden onChange={handleFileSelect} accept="image/*,application/pdf" />
            </Button>

            {selectedFile && (
              <Typography variant="body2" sx={{ mb: 2, color: '#00C48C' }}>
                Selected: {selectedFile.name}
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleUpload}
              disabled={uploading || !selectedFile || !docNumber}
              sx={{ background: 'linear-gradient(135deg, #11C1EC, #09486D)' }}
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Submitted Documents</Typography>

            {documents.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#7F839C' }}>No documents submitted yet</Typography>
            ) : (
              <List>
                {documents.map((doc) => (
                  <ListItem key={doc.id} sx={{ mb: 1, bgcolor: '#F8F8FC', borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {doc.type.replace('_', ' ').toUpperCase()}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#7F839C' }}>
                        {doc.documentNumber}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={doc.status.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(doc.status),
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                      {doc.status === 'pending' && (
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(doc.id)}
                          disabled={deleting}
                        >
                          <CreditCard fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

// Notifications Page Component
const NotificationsPageComponent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { notifications, unreadCount, isLoading, currentPage, totalPages, error: notifError } = useSelector((state: RootState) => state.notifications);

  useEffect(() => {
    dispatch(fetchNotifications({ page: 1, limit: 20 }));
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  const handleMarkAsRead = async (notificationId: string) => {
    await dispatch(markAsRead(notificationId));
  };

  const handleMarkAllAsRead = async () => {
    await dispatch(markAllAsRead());
  };

  const handleDelete = async (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await dispatch(deleteNotification(notificationId));
  };

  const handleNotificationClick = async (notification: any) => {
    // Mark as read if unread
    if (!notification.is_read) {
      await dispatch(markAsRead(notification.id));
    }
    // Navigate to transaction detail if this is a transaction notification with a transactionId
    if (notification.type === 'transaction' && notification.data?.transactionId) {
      navigate(`/transaction/${notification.data.transactionId}`);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'transaction':
        return <Send sx={{ color: '#11C1EC' }} />;
      case 'security':
        return <Security sx={{ color: '#FF4757' }} />;
      case 'kyc':
        return <CheckCircle sx={{ color: '#00C48C' }} />;
      case 'system':
        return <AccountBalance sx={{ color: '#FFA500' }} />;
      default:
        return <NotificationsIcon />;
    }
  };

  const formatAmount = (amount?: number, currency?: string) => {
    if (!amount || !currency) return '';
    return `${currency} ${Number(amount).toLocaleString()}`;
  };

  const getNotificationSummary = (notification: any) => {
    const data = notification.data;
    if (!data || notification.type !== 'transaction') return null;

    const amount = formatAmount(data.amount, data.currency);
    if (data.type === 'receive') {
      const sender = data.senderName || data.sender || '';
      return sender ? `${sender} sent you ${amount}` : `You received ${amount}`;
    } else if (data.type === 'transfer') {
      const recipient = data.recipientName || data.recipient || '';
      return recipient ? `You sent ${amount} to ${recipient}` : `You sent ${amount}`;
    }
    return null;
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    dispatch(setCurrentPage(page));
    dispatch(fetchNotifications({ page, limit: 20 }));
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#202647', fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>
          Notifications
        </Typography>
        {unreadCount > 0 && (
          <Button
            size="small"
            onClick={handleMarkAllAsRead}
            sx={{ color: '#11C1EC', textTransform: 'none' }}
          >
            Mark all as read
          </Button>
        )}
      </Box>

      {notifError && <Alert severity="error" sx={{ mb: 2 }}>{notifError}</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : notifications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <NotificationsIcon sx={{ fontSize: 48, color: '#11C1EC', mb: 2 }} />
          <Typography variant="h6">No notifications yet</Typography>
        </Paper>
      ) : (
        <Box>
          <List>
            {notifications.map((notification) => {
              const isUnread = !notification.is_read;
              const summary = getNotificationSummary(notification);
              const isClickable = notification.type === 'transaction' && notification.data?.transactionId;

              return (
                <Paper
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 2,
                    bgcolor: isUnread ? 'rgba(17, 193, 236, 0.05)' : 'transparent',
                    borderLeft: isUnread ? '4px solid #11C1EC' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    cursor: isClickable ? 'pointer' : 'default',
                    transition: 'background-color 0.15s',
                    '&:hover': isClickable ? { bgcolor: 'rgba(17, 193, 236, 0.08)' } : {},
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                    <Box sx={{ mt: 0.5 }}>{getNotificationIcon(notification.type)}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {notification.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#272A3A', mt: 0.5, fontWeight: summary ? 500 : 400 }}>
                        {summary || notification.message}
                      </Typography>
                      {summary && notification.message !== summary && (
                        <Typography variant="caption" sx={{ color: '#7F839C', mt: 0.25, display: 'block' }}>
                          {notification.message}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: '#A0A3B5', mt: 1, display: 'block' }}>
                        {new Date(notification.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {isUnread && (
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notification.id); }}
                        title="Mark as read"
                      >
                        <CheckCircle fontSize="small" sx={{ color: '#11C1EC' }} />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleDelete(notification.id, e)}
                      title="Delete"
                    >
                      <CreditCard fontSize="small" sx={{ color: '#FF4757' }} />
                    </IconButton>
                  </Box>
                </Paper>
              );
            })}
          </List>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export {
  EmailVerificationPage,
  ProfileCompletionPage,
  CardsPage,
  AddCardPage,
  SendMoneyPage,
  TransactionHistoryPage,
  BankAccountsPage,
  AddBankAccountPage,
  ProfilePage,
  SettingsPage,
  QRCodePage,
  TransactionDetailPage,
  NotFoundPage,
  KYCPageComponent,
  NotificationsPageComponent,
};
